import React, { useEffect, useRef } from "react";
import { placeToAddress } from "../lib/placeToAddress";

/**
 * The address search box: Google Places Autocomplete on a plain input.
 *
 * The Maps script (with `libraries=places`) is loaded once in public/index.html.
 * When it isn't available the box is just a text input — nothing fills in, and
 * the fields below it still work by hand.
 *
 * @param {Function} [onAddress]   (parsed, place) => void — `parsed` is
 *   placeToAddress(place): street / city / state / postal_code / country /
 *   latitude / longitude. What the onboarding forms use to fill themselves.
 * @param {Function} [getLocation] (longitude, latitude, formattedAddress) => void —
 *   the older callback, kept for callers that only want coordinates.
 * @param {string}   [placeholder]
 */
const PlaceSearch = (props) => {
  const autoCompleteRef = useRef();
  const inputRef = useRef();
  // The place_changed listener is registered once; read the latest handlers
  // through a ref so a re-render can't leave it calling stale ones.
  const propsRef = useRef(props);
  propsRef.current = props;

  useEffect(() => {
    const g = window.google;
    if (!(g && g.maps && g.maps.places && g.maps.places.Autocomplete)) return undefined;

    autoCompleteRef.current = new g.maps.places.Autocomplete(inputRef.current, {
      // Only the pieces we read. An un-fielded request returns (and bills for)
      // every Place field.
      fields: ["address_components", "formatted_address", "geometry", "name"],
      componentRestrictions: { country: ["ca", "us"] },
    });

    const listener = autoCompleteRef.current.addListener("place_changed", () => {
      const place = autoCompleteRef.current.getPlace();
      if (!(place && place.geometry && place.geometry.location)) return;
      const parsed = placeToAddress(place);
      const { getLocation, onAddress } = propsRef.current;
      if (getLocation) getLocation(parsed.longitude, parsed.latitude, parsed.formatted_address);
      if (onAddress) onAddress(parsed, place);
    });

    return () => {
      if (listener && listener.remove) listener.remove();
    };
  }, []);

  return (
    <div className="start-self-algin" style={{ width: "100%" }}>
      <input
        autoComplete="off"
        style={{ width: "100%", height: 39, borderRadius: 5 }}
        className="ant-input-search-address"
        ref={inputRef}
        placeholder={props.placeholder || "Search address"}
      />
    </div>
  );
};

export default PlaceSearch;
